import React, { type ReactNode, createElement, useMemo } from 'react';
import { chain } from 'icepick';
import {
	CORE_PROJECT,
	projectTypeToCanonicalId,
	SERVICE_DESK_PROJECT,
	SOFTWARE_PROJECT,
	type ProjectType,
} from '@atlassian/jira-common-constants/src/project-types';
import { useIntl } from '@atlassian/jira-intl';
import type { LicensedProducts } from '@atlassian/jira-shared-types/src/tenant-context';
import { useLicensedProducts } from '@atlassian/jira-tenant-context-controller';
import {
	ASANA_LEARN_MORE_ABOUT_PROJECT_SETUP,
	DEFAULT_PROJECT_CREATE_ROUTE,
	LEARN_MORE_ABOUT_ISSUES,
	LEARN_MORE_ABOUT_PROJECT_HIERARCHY,
	LEARN_MORE_ABOUT_GENERATE_MONDAY_API_TOKEN,
	MAPPING_FIELDS_INFO_LINK,
	MONDAY_IMPORT_APP_DOWNLOAD_URL,
	MONDAY_LEARN_MORE_ABOUT_FIELDS_MAPPING,
	MONDAY_LEARN_MORE_ABOUT_PROJECT_SETUP,
	JIRA_LEARN_MORE_ABOUT_PROJECT_SETUP,
	JIRA_FIELD_MAPPING,
	CLICKUP_LEARN_MORE_ABOUT_PROJECT_SETUP,
	CLICKUP_LEARN_MORE_ABOUT_FIELDS_MAPPING,
	CLICKUP_LEARN_MORE_ABOUT_PROJECT_HIERARCHY,
	CLICK_UP_RATE_LIMIT,
	ASANA_LEARN_MORE_ABOUT_FIELDS_MAPPING,
	LEARN_MORE_ABOUT_ISSUES_SOFTWARE,
	ASANA_LEARN_MORE_ABOUT_FIELDS_MAPPING_SOFTWARE,
	ASANA_LEARN_MORE_ABOUT_PROJECT_SETUP_SOFTWARE,
} from '../constants';
import { LOCALES, TIMEZONES } from '../constants/date';
import {
	type ValidationErrorType,
	INVALID_DATE,
	INVALID_DATETIME,
	INVALID_NUMBER,
	INVALID_USER_ID,
	INVALID_CASCADE_SELECT_FORMAT,
	INVALID_WORKLOG_FORMAT,
	INVALID_COMMENT_FORMAT,
	EMPTY_SUMMARY,
	INVALID_ISSUE_ID,
} from '../constants/errors';
import {
	ISSUEID_FIELDTYPE,
	ISSUE_LINKS_FIELDTYPE,
	TEXT_FIELDTYPE,
	type FieldId,
} from '../constants/fields';

import {
	type SupportedThirdParty,
	MONDAY,
	ASANA,
	JIRA_SOFTWARE,
	CLICK_UP,
	JIRA_BUSINESS,
} from '../constants/import-type';
import { ProjectTypes } from '../constants/project';
import { DEFAULT_LOCALE, DEFAULT_TIMEZONE } from '../constants/state';
import type {
	ColumnName,
	ColumnState,
	MappingState,
	Column,
	AdvancedOptions,
	DateProperties,
	SelectOption,
	IssueLinkColumn,
	ColumnErrors,
	CSVUserEmail,
} from '../types';
import messages from './messages';

export { useContentForThirdParty } from './content-for-third-party';

const getDefaultField = (columnName: ColumnName) => ({
	fieldId: null,
	fieldType: TEXT_FIELDTYPE,
	fieldName: columnName,
});

export const getNewValidationState = (
	mappingState: MappingState,
	columnName: ColumnName,
	mappedLinkFieldsCount: number,
	fieldId?: FieldId | null,
) => {
	const newValidationErrors = mappingState.validationError;
	if (newValidationErrors?.columnErrors?.[columnName]) {
		delete newValidationErrors?.columnErrors?.[columnName];
	}

	if (fieldId && newValidationErrors?.missingFields?.includes(fieldId)) {
		const missingFieldIndex = newValidationErrors?.missingFields.indexOf(fieldId);
		newValidationErrors?.missingFields.splice(missingFieldIndex, 1);
	}

	// Remove issue_id missing error if there are no issue_links mapped anymore.
	if (
		mappedLinkFieldsCount === 0 &&
		newValidationErrors?.missingFields?.includes(ISSUEID_FIELDTYPE)
	) {
		const missingFieldIndex = newValidationErrors?.missingFields.indexOf(ISSUEID_FIELDTYPE);
		newValidationErrors?.missingFields.splice(missingFieldIndex, 1);
	}

	return newValidationErrors;
};

const getUpdatedVisibleColumnEnsuringUniqueField = (
	mappingState: MappingState,
	columnName: ColumnName,
): ColumnState => {
	const column = mappingState.columns[columnName];

	if (!!column.field?.fieldId && mappingState.mappedJiraFieldIds.has(column.field.fieldId)) {
		return {
			...column,
			field: getDefaultField(columnName),
			isVisible: true,
		};
	}

	return { ...column, isVisible: true };
};

export const makeColumnVisible = (
	mappingState: MappingState,
	columnName: ColumnName,
): MappingState => {
	const updatedColumn = getUpdatedVisibleColumnEnsuringUniqueField(mappingState, columnName);
	const updatedMappedJiraFieldIds = mappingState.mappedJiraFieldIds;

	if (updatedColumn.field?.fieldId) {
		updatedMappedJiraFieldIds.add(updatedColumn.field.fieldId);
	}

	return chain(mappingState)
		.setIn(['columns', columnName], updatedColumn)
		.setIn(['mappedJiraFieldIds'], updatedMappedJiraFieldIds)
		.setIn(
			['validationError'],
			getNewValidationState(mappingState, columnName, mappingState.mappedLinkFieldsCount),
		)
		.value();
};

export const makeColumnHidden = (
	mappingState: MappingState,
	columnName: ColumnName,
): MappingState => {
	const updatedColumn = {
		...mappingState.columns[columnName],
		isVisible: false,
	};
	const updatedMappedJiraFieldIds = mappingState.mappedJiraFieldIds;

	if (updatedColumn.field?.fieldId) {
		updatedMappedJiraFieldIds.delete(updatedColumn.field.fieldId);
	}

	return chain(mappingState)
		.setIn(['columns', columnName], updatedColumn)
		.setIn(['mappedJiraFieldIds'], updatedMappedJiraFieldIds)
		.setIn(
			['validationError'],
			getNewValidationState(mappingState, columnName, mappingState.mappedLinkFieldsCount),
		)
		.value();
};

export const setFieldIncluded = (
	mappingState: MappingState,
	columnName: ColumnName,
	isIncluded: boolean,
): MappingState => {
	const updatedColumn = {
		...mappingState.columns[columnName],
		isIncluded,
	};
	const updatedMappedJiraFieldIds = mappingState.mappedJiraFieldIds;
	let { mappedLinkFieldsCount } = mappingState;

	if (!isIncluded && updatedColumn.field) {
		updatedColumn.field.fieldId && updatedMappedJiraFieldIds.delete(updatedColumn.field.fieldId);
		if (updatedColumn.field.fieldType === ISSUE_LINKS_FIELDTYPE) {
			mappedLinkFieldsCount--;
		}
		updatedColumn.field = null;
	}

	return chain(mappingState)
		.setIn(['columns', columnName], updatedColumn)
		.setIn(['mappedJiraFieldIds'], updatedMappedJiraFieldIds)
		.setIn(
			['validationError'],
			getNewValidationState(mappingState, columnName, mappedLinkFieldsCount),
		)
		.setIn(['mappedLinkFieldsCount'], mappedLinkFieldsCount)
		.setIn(['userMapping'], null) // reset userMapping if field is changed
		.value();
};

export const transformMappingStateToFieldMapping = (mappingState: MappingState): Column[] =>
	mappingState.columnOrder
		.map((name) => mappingState.columns[name])
		.filter((col) => col.isVisible)
		.filter((col) => !!col.field && col.field.fieldType !== ISSUE_LINKS_FIELDTYPE)
		.map(({ columnName, field }) => ({
			columnName,
			field,
		}));

export const transformMappingStateToIssueLinkMapping = (
	mappingState: MappingState,
): IssueLinkColumn[] => {
	const issueLinkColumns: IssueLinkColumn[] = [];
	mappingState.columnOrder
		.map((name) => mappingState.columns[name])
		.filter((col) => col.isVisible)
		.filter((col) => !!col.field && col.field.fieldType === ISSUE_LINKS_FIELDTYPE)
		.forEach(({ columnName, field }) => {
			if (field && field.fieldDirection && field.fieldId)
				issueLinkColumns.push({
					columnName,
					issueLink: {
						linkName: field.fieldName,
						linkId: field.fieldId,
						linkDirection: field.fieldDirection,
					},
				});
		});
	return issueLinkColumns;
};

export const transformToDateProperties = ({
	dateFormat,
	timeZoneOption,
	localeOption,
}: AdvancedOptions): DateProperties => ({
	dateFormat,
	timeZone: timeZoneOption.value,
	locale: localeOption.value,
});

export const transformToUserEmailMappings = (mappingState: MappingState): CSVUserEmail[] =>
	mappingState.userMapping
		? Object.values(mappingState.userMapping).filter((user) => !!user.userValue)
		: [];

export const useGetValidationErrorMessage = () => {
	const { formatMessage } = useIntl();

	const getValidationErrorMessage = (
		errorType: ValidationErrorType,
		showLink = false,
	): string | ReactNode => {
		switch (errorType) {
			case INVALID_DATE:
				return formatMessage(messages.invalidDate);
			case INVALID_DATETIME:
				return formatMessage(messages.invalidDateTime);
			case INVALID_NUMBER:
				return formatMessage(messages.invalidNumber);
			case INVALID_USER_ID:
				return formatMessage(messages.invalidUserId);
			case INVALID_CASCADE_SELECT_FORMAT:
				return formatMessage(messages.invalidCascadingSelectFormat);
			case INVALID_WORKLOG_FORMAT:
				return showLink
					? formatMessage(messages.invalidWorkLogFormatWithLink, {
							link: (
								<a href={MAPPING_FIELDS_INFO_LINK} target="_blank">
									{formatMessage(messages.invalidWorkLogFormatLinkText)}
								</a>
							),
						})
					: formatMessage(messages.invalidWorkLogFormatWithoutLink);
			case INVALID_COMMENT_FORMAT:
				return formatMessage(messages.invalidCommentFormat);
			case EMPTY_SUMMARY:
				return formatMessage(messages.emptySummary);
			default:
				return formatMessage(messages.genericErrors);
		}
	};

	return { getValidationErrorMessage };
};

export const hasTableColumnErrors = (mappingState: MappingState | null): boolean => {
	const columnErrors = mappingState?.validationError?.columnErrors;
	return !!columnErrors && Object.keys(columnErrors).length > 0;
};

type ColumnErrorsGroup = {
	issueLinkingColumnErrors: ColumnErrors;
	otherColumnErrors: ColumnErrors;
};
export const getColumnErrors = (mappingState: MappingState | null): ColumnErrorsGroup => {
	const issueLinkingColumnErrors: ColumnErrors = {};
	const otherColumnErrors: ColumnErrors = {};
	Object.entries(mappingState?.validationError?.columnErrors || {}).forEach(
		([name, errorType]: [ColumnName, ValidationErrorType]) => {
			if (errorType === INVALID_ISSUE_ID) issueLinkingColumnErrors[name] = errorType;
			else otherColumnErrors[name] = errorType;
		},
	);
	return { issueLinkingColumnErrors, otherColumnErrors };
};

export const hasMissingFields = (mappingState: MappingState | null): boolean => {
	const missingFields = mappingState?.validationError?.missingFields;
	return !!missingFields && missingFields.length > 0;
};
export { messages };

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const createGenericComponent = (name: string, customProps?: any) => (props: any) =>
	createElement(name, { ...customProps, ...props }, props.children);

export const getLocaleOptionForUserLocale = (locale: string | undefined): SelectOption =>
	LOCALES.find((option) => !!locale && locale.startsWith(option.value)) || DEFAULT_LOCALE;

export const getTimeZoneOptionForUserTimeZone = (timeZone: string | undefined): SelectOption =>
	TIMEZONES.find((option) => option.value === timeZone) || DEFAULT_TIMEZONE;

export const getSiteUrl = () => `https://${window.location.hostname}`;

const PRODUCTS_INCLUDED_WITH_LICENSE = {
	[SOFTWARE_PROJECT]: [SOFTWARE_PROJECT, CORE_PROJECT],
	[SERVICE_DESK_PROJECT]: [SERVICE_DESK_PROJECT, CORE_PROJECT],
	[CORE_PROJECT]: [CORE_PROJECT],
};

export const getSetOfProductsAvailable = (licensedProducts: LicensedProducts) =>
	new Set(
		[CORE_PROJECT, SOFTWARE_PROJECT, SERVICE_DESK_PROJECT]
			.filter((type) => licensedProducts[projectTypeToCanonicalId(type)])
			.flatMap((type) => PRODUCTS_INCLUDED_WITH_LICENSE[type]),
	);

export const hasOnlyJWMLicense = (
	setOfProductsAvailable: Set<
		typeof SOFTWARE_PROJECT | typeof SERVICE_DESK_PROJECT | typeof CORE_PROJECT
	>,
) => setOfProductsAvailable.size === 1 && setOfProductsAvailable.has(CORE_PROJECT);

export const useIsImportSupportedByLicensedProducts = () => {
	const licensedProducts = useLicensedProducts();

	return !!(
		licensedProducts[projectTypeToCanonicalId(SOFTWARE_PROJECT)] ||
		licensedProducts[projectTypeToCanonicalId(CORE_PROJECT)] ||
		licensedProducts[projectTypeToCanonicalId(SERVICE_DESK_PROJECT)]
	);
};

export const useHasJSWLicense = () => {
	const licensedProducts = useLicensedProducts();
	return Boolean(licensedProducts[projectTypeToCanonicalId(SOFTWARE_PROJECT)]);
};

export const useUrlForThirdParty = (
	thirdParty?: SupportedThirdParty,
	projectType?: ProjectType | null,
) =>
	useMemo(() => {
		switch (thirdParty) {
			case MONDAY:
				return {
					appDownloadUrl: MONDAY_IMPORT_APP_DOWNLOAD_URL,
					reviewScreenDescriptionURL: MONDAY_LEARN_MORE_ABOUT_PROJECT_SETUP,
					hierarchyPageDescriptionURL: LEARN_MORE_ABOUT_PROJECT_HIERARCHY,
					fieldURL: MONDAY_LEARN_MORE_ABOUT_FIELDS_MAPPING,
					customFieldURL: '',
					generateAPIToken: LEARN_MORE_ABOUT_GENERATE_MONDAY_API_TOKEN,
					fieldMappingURL: '',
					rateLimitURL: '',
					statusMappingURL: '',
				};
			case ASANA:
				return {
					appDownloadUrl: '',
					reviewScreenDescriptionURL: ASANA_LEARN_MORE_ABOUT_PROJECT_SETUP,
					hierarchyPageDescriptionURL: LEARN_MORE_ABOUT_ISSUES,
					fieldURL: ASANA_LEARN_MORE_ABOUT_FIELDS_MAPPING,
					customFieldURL: '',
					generateAPIToken: '',
					fieldMappingURL: '',
					rateLimitURL: '',
					statusMappingURL: '',
					...(projectType === ProjectTypes.SOFTWARE && {
						reviewScreenDescriptionURL: ASANA_LEARN_MORE_ABOUT_PROJECT_SETUP_SOFTWARE,
						hierarchyPageDescriptionURL: LEARN_MORE_ABOUT_ISSUES_SOFTWARE,
						fieldURL: ASANA_LEARN_MORE_ABOUT_FIELDS_MAPPING_SOFTWARE,
					}),
				};
			case JIRA_SOFTWARE:
				return {
					appDownloadUrl: '',
					reviewScreenDescriptionURL: JIRA_LEARN_MORE_ABOUT_PROJECT_SETUP,
					hierarchyPageDescriptionURL: '',
					fieldURL: '',
					customFieldURL: '',
					generateAPIToken: '',
					fieldMappingURL: JIRA_FIELD_MAPPING,
					rateLimitURL: '',
					statusMappingURL: '',
				};
			case CLICK_UP:
				return {
					appDownloadUrl: '',
					reviewScreenDescriptionURL: CLICKUP_LEARN_MORE_ABOUT_PROJECT_SETUP,
					hierarchyPageDescriptionURL: CLICKUP_LEARN_MORE_ABOUT_PROJECT_HIERARCHY,
					fieldURL: CLICKUP_LEARN_MORE_ABOUT_FIELDS_MAPPING,
					customFieldURL: '',
					generateAPIToken: '',
					fieldMappingURL: CLICKUP_LEARN_MORE_ABOUT_FIELDS_MAPPING,
					statusMappingURL: '',
					rateLimitURL: CLICK_UP_RATE_LIMIT,
				};
			case JIRA_BUSINESS:
				// TODO: Update to new urls when available
				return {
					appDownloadUrl: '',
					reviewScreenDescriptionURL: JIRA_LEARN_MORE_ABOUT_PROJECT_SETUP,
					hierarchyPageDescriptionURL: '',
					fieldURL: JIRA_FIELD_MAPPING,
					customFieldURL: '',
					generateAPIToken: '',
					fieldMappingURL: '',
					rateLimitURL: '',
					statusMappingURL: '',
				};
			default:
				return {
					appDownloadUrl: '',
					reviewScreenDescriptionURL: '',
					hierarchyPageDescriptionURL: '',
					fieldURL: '',
					customFieldURL: '',
					generateAPIToken: '',
					fieldMappingURL: '',
					rateLimitURL: '',
					statusMappingURL: '',
				};
		}
	}, [projectType, thirdParty]);

export const redirectToProjectTemplatesScreen = () => {
	window.location.href = DEFAULT_PROJECT_CREATE_ROUTE;
};
